import {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {PublishLayout} from '../components/PublishLayout';
import {AlertTriangle, Check, Link2, Plus, Trash2, Users} from 'lucide-react';
import {ShareModal} from '../components/ShareModal';
import {GenreSelector} from '../components/GenreSelector';
import {CountrySelect} from '../components/CountrySelect';
import {Button} from "../components/ui/button";
import {Input} from "../components/ui/input";
import {Textarea} from "../components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {Label} from "../components/ui/label";
import {Switch} from "../components/ui/switch";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue,} from "../components/ui/select";
import {cn} from '../lib/utils';
import {useAuth} from '../contexts/AuthContext';
import {
  createNewShow,
  fetchShow,
  fetchShowCountryBlacklist,
  ShowDTO,
  ShowType,
  updateShow,
  updateShowCountryBlacklist,
  updateShowMetadata
} from '../services/showService';
import {fetchCountryWhitelists} from '../services/countryWhitelistService';
import {getLangCode, LANG_CODES, PlatformType, ShowContentType, ShowMetaDataType} from '../types/Show';
import type {CountryWhitelist} from '../types/CountryWhitelist';
import {CharacterGender} from '../constants/constants';
import {COUNTRIES} from '../constants/Countries';
import {auth} from '../lib/firebase';
import {toast} from 'sonner';
import styles from '../styles/Publish.module.css';

const languages = Object.values(LANG_CODES).map(code => ({
  value: code,
  label: getLangCode(code)
}));

const maturityRatings = [
  {value: "0", label: "U (All Ages)"},
  {value: "7", label: "U/A 7+"},
  {value: "13", label: "U/A 13+"},
  {value: "16", label: "U/A 16+"},
  {value: "18", label: "A (18+)"}
];

const showContentTypeOptions = Object.values(ShowContentType).map(value => ({
  value,
  label: value
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}));

const isShowContentType = (value?: string): value is ShowContentType =>
  !!value && Object.values(ShowContentType).includes(value as ShowContentType);

interface PublishState {
  contentType: 'dashreel';
  details: {
    title: string;
    creatorName: string;
    authorName: string;
    publisherName: string;
    description: string;
    language: string;
    showType: string;
    contentOrigin: string;
    visualStyle: string;
    hasSubtitleSupport: boolean;
    subtitleLanguages: string[];
    genres: string[];
    tropes: string[];
    tags: string[];
    maturityRating: string;
    isTrialShow: boolean;
    isFastPassEnabled: boolean;
    isAIShow: boolean;
    category: string;
    whitelist: string;
    actorGenderList: Array<{ characterName: string; gender: string }>;
    originCountry: string | null;
    platformTypes: PlatformType[];
    showContentType: ShowContentType;
  };
}

export default function Publish() {
  const navigate = useNavigate();
  const { showId } = useParams();
  const { isAuth, loading } = useAuth();
  
  // Debug: Log when component mounts/updates
  console.log('Publish component rendered with showId:', showId, 'isAuth:', isAuth, 'loading:', loading);
  
  // Validate showId - if it exists, it should be a valid show ID (starts with SHO)
  useEffect(() => {
    if (showId && !showId.startsWith('SHO')) {
      console.error('Invalid showId detected:', showId, 'Redirecting to /publish');
      navigate('/publish', { replace: true });
      return;
    }
  }, [showId, navigate]);
  
  // Initialize state hooks first (before any early returns)
  const [state, setState] = useState<PublishState>({
    contentType: 'dashreel',
    details: {
      title: '',
      creatorName: 'DreamWeaver',
      authorName: '',
      publisherName: '',
      description: '',
      language: '',
      showType: '',
      contentOrigin: '',
      visualStyle: '',
      hasSubtitleSupport: false,
      subtitleLanguages: [],
      genres: [],
      tropes: [],
      tags: [],
      maturityRating: '',
      isTrialShow: false,
      isFastPassEnabled: false,
      isAIShow: false,
      category: '',
      whitelist: '',
      actorGenderList: [],
      originCountry: null,
      platformTypes: [PlatformType.DASHREEL],
      showContentType: ShowContentType.DIRECT_DISTRIBUTION
    }
  });
  const [showShareModal, setShowShareModal] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
  const [isLoadingShow, setIsLoadingShow] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateShowInfo, setDuplicateShowInfo] = useState<any>(null);
  const [pendingAction, setPendingAction] = useState<'create' | 'createAndContinue' | null>(null);
  const [pendingShowData, setPendingShowData] = useState<ShowDTO | null>(null);
  const [whitelists, setWhitelists] = useState<CountryWhitelist[]>([]);
  const [loadedBlacklist, setLoadedBlacklist] = useState<string[] | null>(null);

  // Helper functions for whitelist <-> blacklist conversion
  // Map whitelist categories to blacklist country codes
  // Whitelist = countries where show IS available
  // Blacklist = countries where show is BLOCKED (NOT available)
  const mapWhitelistToBlacklist = (whitelistId: string): string[] => {
    const selectedWhitelist = whitelists.find(wl => wl.id === whitelistId);

    if (!selectedWhitelist) return [];

    // Special handling for '*' marker (only 'All Countries' uses this)
    if (selectedWhitelist.countryCodes.includes('*')) {
      // Show available everywhere → Block nothing
      return [];
    }

    // Regular whitelist: convert allowed countries to blacklist
    // Blacklist = all countries EXCEPT those in the whitelist
    const allCountryCodes = COUNTRIES.map(c => c.code);
    return allCountryCodes.filter(code => !selectedWhitelist.countryCodes.includes(code));
  };

  // Resolve the default whitelist preset. Backend field is `isDefault` in Kotlin
  // but Jackson serializes it as `default` on the wire — read what the API returns.
  const findDefaultWhitelistId = (): string =>
    whitelists.find(wl => wl.default)?.id || '';

  // Reverse mapping: Convert blacklist to whitelist ID for display
  const mapBlacklistToWhitelist = (blacklist: string[]): string => {
    // If no blacklist, use the default (9-country) preset
    if (blacklist.length === 0) {
      return findDefaultWhitelistId();
    }

    const allCountryCodes = COUNTRIES.map(c => c.code);
    const allowedCountries = allCountryCodes.filter(code => !blacklist.includes(code));

    // Check if it matches any whitelist preset
    for (const wl of whitelists) {
      // Special handling for '*' marker (only 'All Countries' uses this)
      if (wl.countryCodes.includes('*')) {
        if (blacklist.length === 0) {
          return wl.id;
        }
      } else {
        // Regular whitelist: compare country codes
        const whitelistCodes = [...wl.countryCodes].sort();
        const allowedCodes = [...allowedCountries].sort();
        if (JSON.stringify(whitelistCodes) === JSON.stringify(allowedCodes)) {
          return wl.id;
        }
      }
    }

    // Default fallback: the 9-country default preset
    return findDefaultWhitelistId();
  };

  // Redirect to home if not authenticated
  useEffect(() => {
    if (!loading && !isAuth) {
      navigate('/', { replace: true });
    }
  }, [isAuth, loading, navigate]);

  // Fetch country whitelists after authentication
  useEffect(() => {
    const loadWhitelists = async () => {
      console.log('loadWhitelists called', { isAuth, loading });
      if (isAuth && !loading) {
        console.log('Fetching whitelists...');
        const data = await fetchCountryWhitelists();
        console.log('Whitelists fetched:', data);
        setWhitelists(data);
      }
    };
    loadWhitelists();
  }, [isAuth, loading]);

  // Fetch show details when showId is present
  useEffect(() => {
    const fetchShowDetails = async () => {
      if (showId && isAuth && !loading) {
        try {
          setIsLoadingShow(true);
          console.log('Fetching show details for:', showId);
          console.log('Current auth user:', auth.currentUser?.uid);
          console.log('Auth token available:', !!auth.currentUser);
          
          // Wait a bit to ensure Firebase auth is fully ready
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Double check that we can get a fresh token
          if (!auth.currentUser) {
            throw new Error('No authenticated user found');
          }
          
          try {
            const token = await auth.currentUser.getIdToken();
            console.log('Got fresh auth token:', token ? 'Yes' : 'No');
          } catch (tokenError) {
            console.error('Failed to get auth token:', tokenError);
            throw new Error('Failed to get authentication token');
          }
          
          const showResponse = await fetchShow(showId, {
            requestedMetadata: [
              'CONTINUE_READING_THUMBNAIL_V2',
              'BANNER_THUMBNAIL_V2',
              'WIDGET_THUMBNAIL_V2',
              'LANGUAGE',
              'MATURITY_RATING',
              'IS_TRIAL_SHOW',
              'FAST_PASS_ENABLED',
              'SUBTITLE_DISABLED',
              'SUBTITLE_LANGUAGE',
              'ACTOR_GENDER',
              'IS_AI_SHOW',
              'CONTENT_ORIGIN',
              'VISUAL_STYLE',
              'SHOW_CONTENT_TYPE'
            ],
            includeEpisodes: true,
            includeEpisodeUserMapping: true,
          });
          
          // Update form state with fetched show details
          const languageMetadata = showResponse.metadata?.find(m => m.type === 'LANGUAGE');
          const maturityMetadata = showResponse.metadata?.find(m => m.type === 'MATURITY_RATING');
          const trialShowMetadata = showResponse.metadata?.find(m => m.type === 'IS_TRIAL_SHOW');
          const fastPassMetadata = showResponse.metadata?.find(m => m.type === 'FAST_PASS_ENABLED');
          const subtitleDisabledMetadata = showResponse.metadata?.find(m => m.type === 'SUBTITLE_DISABLED');
          const subtitleLanguageMetadata = showResponse.metadata?.find(m => m.type === 'SUBTITLE_LANGUAGE');
          const actorGenderMetadata = showResponse.metadata?.find(m => m.type === 'ACTOR_GENDER');
          const isAIShowMetadata = showResponse.metadata?.find(m => m.type === 'IS_AI_SHOW');
          const contentOriginMetadata = showResponse.metadata?.find(m => m.type === 'CONTENT_ORIGIN');
          const visualStyleMetadata = showResponse.metadata?.find(m => m.type === 'VISUAL_STYLE');
          const showContentTypeMetadata = showResponse.metadata?.find(m => m.type === 'SHOW_CONTENT_TYPE');

          // Parse actor gender data
          let actorGenderList: Array<{ characterName: string; gender: string }> = [];
          if (actorGenderMetadata?.value) {
            try {
              const parsedData = JSON.parse(actorGenderMetadata.value);
              // Convert object format {"John_Smith": "male"} to array format
              if (typeof parsedData === 'object' && !Array.isArray(parsedData)) {
                actorGenderList = Object.entries(parsedData).map(([characterName, gender]) => ({
                  characterName,
                  gender: gender as string
                }));
              }
            } catch (error) {
              console.error('Failed to parse actor gender data:', error);
              actorGenderList = [];
            }
          }
          
          // Fetch country blacklist and store it
          // Mapping to whitelist will happen in a separate useEffect once whitelists are loaded
          try {
            const countryBlacklist = await fetchShowCountryBlacklist(showId);
            console.log('Country blacklist loaded:', countryBlacklist);
            setLoadedBlacklist(countryBlacklist);
          } catch (error) {
            console.error('Failed to fetch country blacklist:', error);
            setLoadedBlacklist([]);
          }

          setState(prev => ({
            ...prev,
            details: {
              ...prev.details,
              title: showResponse.name || '',
              creatorName: showResponse.creator || 'DreamWeaver',
              authorName: showResponse.author || '',
              publisherName: showResponse.publisher || '',
              description: showResponse.description || '',
              language: languageMetadata?.value || '',
              showType: showResponse.type?.toLowerCase() || '',
              contentOrigin: contentOriginMetadata?.value || '',
              visualStyle: visualStyleMetadata?.value || '',
              genres: showResponse.genre ? showResponse.genre.split(',') : [],
              tropes: showResponse.trope ? showResponse.trope.split(',') : [],
              tags: showResponse.tag ? showResponse.tag.split(',') : [],
              maturityRating: maturityMetadata?.value || '',
              isTrialShow: trialShowMetadata?.value === 'true',
              isFastPassEnabled: fastPassMetadata?.value === 'true',
              isAIShow: isAIShowMetadata?.value === 'true',
              hasSubtitleSupport: subtitleDisabledMetadata?.value !== 'true',
              subtitleLanguages: subtitleLanguageMetadata?.value ? subtitleLanguageMetadata.value.split(',') : [],
              actorGenderList: actorGenderList,
              originCountry: showResponse.originCountry || null,
              platformTypes: showResponse.platformTypes?.length ? showResponse.platformTypes : [PlatformType.DASHREEL],
              showContentType: isShowContentType(showContentTypeMetadata?.value)
                ? showContentTypeMetadata.value
                : ShowContentType.DIRECT_DISTRIBUTION,
              // whitelist will be set by a separate useEffect after mapping blacklist
            }
          }));

          console.log('Show details loaded:', showResponse);

        } catch (error) {
          console.error('Failed to fetch show details:', error);
          console.error('Error details:', {
            authUser: auth.currentUser?.uid,
            showId: showId
          });
          toast.error(`Failed to load show details: || 'Unknown error`);
        } finally {
          setIsLoadingShow(false);
        }
      }
    };

    fetchShowDetails();
  }, [showId, isAuth, loading]);

  // Map loaded blacklist to whitelist once both are available
  useEffect(() => {
    if (loadedBlacklist !== null && whitelists.length > 0) {
      const whitelistSelection = mapBlacklistToWhitelist(loadedBlacklist);
      console.log('Mapping blacklist to whitelist:', {
        blacklist: loadedBlacklist,
        whitelistSelection,
        availableWhitelists: whitelists.map(w => ({ id: w.id, displayName: w.displayName }))
      });

      setState(prev => ({
        ...prev,
        details: {
          ...prev.details,
          whitelist: whitelistSelection
        }
      }));
    }
  }, [loadedBlacklist, whitelists]);

  // For new shows (no showId), pre-select the isDefault preset once whitelists load.
  useEffect(() => {
    if (!showId && whitelists.length > 0 && !state.details.whitelist) {
      const defaultId = findDefaultWhitelistId();
      if (defaultId) {
        setState(prev => ({
          ...prev,
          details: { ...prev.details, whitelist: defaultId }
        }));
      }
    }
  }, [showId, whitelists]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save with debounce when form data changes
  useEffect(() => {
    if (!hasUnsavedChanges || !showId || !state.details) return;
    
    const timeoutId = setTimeout(async () => {
      try {
        const details = state.details;
        
        // Prepare show update data (main show fields)
        const showUpdateData: Partial<ShowDTO> = {
          name: details.title,
          creator: details.creatorName,
          author: details.authorName,
          publisher: details.publisherName,
          description: details.description,
          type: mapShowType(details.showType),
          genre: details.genres.join(','),
          trope: details.tropes.join(','),
          tag: details.tags.join(','),
          originCountry: details.originCountry,
          platformTypes: details.platformTypes,
        };

        // Prepare metadata updates (fields that go to metadata endpoint)
        const metadataList = [
          { type: ShowMetaDataType.LANGUAGE, value: details.language },
          { type: ShowMetaDataType.MATURITY_RATING, value: details.maturityRating },
          { type: ShowMetaDataType.IS_TRIAL_SHOW, value: details.isTrialShow ? 'true' : 'false' },
          { type: ShowMetaDataType.FAST_PASS_ENABLED, value: details.isFastPassEnabled ? 'true' : 'false' },
          { type: ShowMetaDataType.IS_AI_SHOW, value: details.isAIShow ? 'true' : 'false' },
          // Add subtitle disabled metadata - hasSubtitleSupport=true means SUBTITLE_DISABLED='false', hasSubtitleSupport=false means SUBTITLE_DISABLED='true'
          { type: ShowMetaDataType.SUBTITLE_DISABLED, value: details.hasSubtitleSupport ? 'false' : 'true' },
          ...(details.subtitleLanguages?.length > 0 ? [{ type: ShowMetaDataType.SUBTITLE_LANGUAGE, value: details.subtitleLanguages.join(',') }] : []),
          ...(details.contentOrigin ? [{ type: ShowMetaDataType.CONTENT_ORIGIN, value: details.contentOrigin }] : []),
          ...(details.contentOrigin === 'ORIGINAL' && details.visualStyle ? [{ type: ShowMetaDataType.VISUAL_STYLE, value: details.visualStyle }] : []),
          { type: ShowMetaDataType.SHOW_CONTENT_TYPE, value: details.showContentType },
        ].filter(metadata => metadata.value);

        // Update show details and metadata
        if (Object.keys(showUpdateData).length > 0) {
          await updateShow(showId, showUpdateData);
        }
        
        if (metadataList.length > 0) {
          await updateShowMetadata(showId, metadataList);
        }

        // Note: Whitelist/blacklist is NOT auto-saved - only saved on explicit action
        // Remove auto-save to prevent immediate API calls on dropdown change

        setHasUnsavedChanges(false);
        console.log('Show details auto-saved');
      } catch (err) {
        console.error('Auto-save failed:', err);
        // Don't show error toast for auto-save failures to avoid being intrusive
      }
    }, 2000); // 2-second debounce

    return () => clearTimeout(timeoutId);
  }, [state.details, hasUnsavedChanges, showId]);

  // Show loading state while checking authentication or loading show
  if (loading || isLoadingShow) {
    return (
      <div className="h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuth) {
    return null;
  }

  const updateState = (updates: Partial<PublishState>) => {
    setState(prev => ({ ...prev, ...updates }));
    // Mark as having unsaved changes when form data is updated
    if (showId && updates.details) {
      setHasUnsavedChanges(true);
    }
  };

  // Map frontend show type values to backend enum values
  const mapShowType = (frontendType: string): ShowType => {
    switch (frontendType.toLowerCase()) {
      case 'ongoing':
        return ShowType.SERIALIZED; // Ongoing shows are serialized
      case 'completed':
        return ShowType.ONE_SHOT; // Completed shows are one-shot
      case 'serialized':
        return ShowType.SERIALIZED;
      case 'one_shot':
        return ShowType.ONE_SHOT;
      default:
        return ShowType.SERIALIZED; // Default to serialized
    }
  };

  const validateForm = () => {
    const errors: Record<string, boolean> = {};
    const details = state.details;
    const genres = details.genres || [];
    const tropes = details.tropes || [];

    // Check mandatory fields
    errors.title = !details.title;
    errors.creatorName = !details.creatorName;
    errors.language = !details.language;
    errors.showType = !details.showType;
    errors.contentOrigin = !details.contentOrigin;  // Required field
    // Visual style is required ONLY when contentOrigin is ORIGINAL
    if (details.contentOrigin === 'ORIGINAL') {
      errors.visualStyle = !details.visualStyle;
    }
    // Subtitle support is always valid as it's a boolean

    setFormErrors(errors);
    return !Object.values(errors).some(Boolean);
  };

  const handleUpdateActorGender = async (newActorGenderList: Array<{ characterName: string; gender: string }>) => {
    // Convert array format to object format {"John_Smith": "male"}
    const actorGenderObject: Record<string, string> = {};
    newActorGenderList.forEach(({ characterName, gender }) => {
      if (characterName.trim()) { // Only add if character name is not empty
        actorGenderObject[characterName.trim()] = gender; // Keep original enum value (Male/Female)
      }
    });

    let updatedMetaData = [{
      type: ShowMetaDataType.ACTOR_GENDER,
      value: JSON.stringify(actorGenderObject),
    }];

    updateState({
      details: { ...state.details, actorGenderList: newActorGenderList }
    });

    if (showId) {
      try {
        await updateShowMetadata(showId, updatedMetaData);
      } catch (error) {
        console.error('Error updating actor gender metadata:', error);
        console.log('Failed payload:', updatedMetaData);
      }
    }
  };

  const addActorGenderEntry = () => {
    const newEntry = { characterName: '', gender: CharacterGender.MALE };
    const updatedList = [...state.details.actorGenderList, newEntry];
    updateState({
      details: { ...state.details, actorGenderList: updatedList }
    });
  };

  const removeActorGenderEntry = (index: number) => {
    const updatedList = state.details.actorGenderList.filter((_, i) => i !== index);
    handleUpdateActorGender(updatedList);
  };

  const updateActorGenderEntry = (index: number, field: 'characterName' | 'gender', value: string) => {
    const updatedList = state.details.actorGenderList.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    updateState({
      details: { ...state.details, actorGenderList: updatedList }
    });

    // Only send request if the updated entry has both characterName and gender
    const updatedEntry = updatedList[index];
    if (updatedEntry.characterName.trim() && updatedEntry.gender) {
      handleUpdateActorGender(updatedList);
    }
  };

  const proceedWithCreate = async (force: boolean = false) => {
    try {
      toast.loading('Creating your show...', { id: 'create-show' });

      // Map form data to ShowDTO
      const showData: ShowDTO = {
        name: state.details.title,
        creator: state.details.creatorName,
        thumbNailImageData: null, // Will be updated later with thumbnails
        description: state.details.description,
        platformType: PlatformType.DASHREEL,
        platformTypes: state.details.platformTypes,
        genre: state.details.genres.join(','), // Convert array to comma-separated string
        trope: state.details.tropes.join(','), // Convert array to comma-separated string
        createdBy: null, // Will be set by backend based on auth
        metadata: [
          // Required metadata fields
          { type: ShowMetaDataType.COMIC_STYLE, value: 'REALISTIC_COMIC' }, // Required field - default style
          { type: ShowMetaDataType.LANGUAGE, value: state.details.language },
          { type: ShowMetaDataType.MATURITY_RATING, value: state.details.maturityRating },
          { type: ShowMetaDataType.IS_TRIAL_SHOW, value: state.details.isTrialShow.toString() },
          { type: ShowMetaDataType.FAST_PASS_ENABLED, value: state.details.isFastPassEnabled.toString() },
          { type: ShowMetaDataType.IS_AI_SHOW, value: state.details.isAIShow.toString() },
          ...(state.details.contentOrigin ? [{ type: ShowMetaDataType.CONTENT_ORIGIN, value: state.details.contentOrigin }] : []),
          ...(state.details.contentOrigin === 'ORIGINAL' && state.details.visualStyle ? [{ type: ShowMetaDataType.VISUAL_STYLE, value: state.details.visualStyle }] : []),
          { type: ShowMetaDataType.SHOW_CONTENT_TYPE, value: state.details.showContentType },
        ],
        type: mapShowType(state.details.showType),
        tag: state.details.tags.join(','),
        cluster: 'DEFAULT', // Default cluster
        publisher: state.details.publisherName || 'STUDIO', // Use publisherName or default
        author: state.details.authorName || state.details.creatorName, // Use authorName or fallback to creatorName
        originCountry: state.details.originCountry,
        force  // Add force flag
      };

      const response = await createNewShow(showData);

      // Check if duplicate warning returned
      if (response.duplicateTitleWarning?.exists) {
        // Show dialog - await user decision
        setDuplicateShowInfo(response.duplicateTitleWarning.existingShow);
        setShowDuplicateDialog(true);
        setPendingShowData(showData);
        toast.dismiss('create-show');
        return;
      }

      // No duplicate - show was created successfully
      if (response.show) {
        // Persist the default whitelist selection on the freshly created show.
        // Failure here shouldn't block navigation — the show exists; user can fix from edit page.
        if (state.details.whitelist) {
          try {
            const blacklistedCountries = mapWhitelistToBlacklist(state.details.whitelist);
            await updateShowCountryBlacklist(response.show.id, blacklistedCountries);
          } catch (error) {
            console.error('Failed to save country blacklist on create:', error);
          }
        }

        toast.success('Show created successfully!', { id: 'create-show' });
        // After creation, stay on the same page but now with the show ID
        navigate(`/publish/${response.show.id}`, { replace: true });
      }

    } catch (error) {
      console.error('Failed to create show:', error);
      toast.error('Failed to create show. Please try again.', { id: 'create-show' });
    }
  };

  const handleCreate = async () => {
    if (!validateForm()) {
      toast.error('Please fill in all mandatory fields before creating');
      return;
    }

    setPendingAction('create');
    await proceedWithCreate();
  };

  const handlePublish = async () => {
    if (!validateForm()) {
      toast.error('Please fill in all mandatory fields before continuing');
      return;
    }

    if (showId) {
      // Save show details before navigating (don't rely on debounced auto-save)
      try {
        const details = state.details;
        const showUpdateData: Partial<ShowDTO> = {
          name: details.title,
          creator: details.creatorName,
          author: details.authorName,
          publisher: details.publisherName,
          description: details.description,
          type: mapShowType(details.showType),
          genre: details.genres.join(','),
          trope: details.tropes.join(','),
          tag: details.tags.join(','),
          originCountry: details.originCountry,
          platformTypes: details.platformTypes,
        };
        await updateShow(showId, showUpdateData);
        await updateShowMetadata(showId, [
          ...(details.contentOrigin ? [{ type: ShowMetaDataType.CONTENT_ORIGIN, value: details.contentOrigin }] : []),
          ...(details.contentOrigin === 'ORIGINAL' && details.visualStyle ? [{ type: ShowMetaDataType.VISUAL_STYLE, value: details.visualStyle }] : []),
          { type: ShowMetaDataType.SHOW_CONTENT_TYPE, value: details.showContentType },
        ]);
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error('Failed to save show details on continue:', error);
        toast.error('Failed to save show details');
        return;
      }

      // Save whitelist/blacklist before navigating
      if (state.details.whitelist) {
        try {
          const blacklistedCountries = mapWhitelistToBlacklist(state.details.whitelist);
          await updateShowCountryBlacklist(showId, blacklistedCountries);
          console.log('Country blacklist saved on continue:', blacklistedCountries);
        } catch (error) {
          console.error('Failed to save country blacklist:', error);
          toast.error('Failed to save country whitelist');
          return; // Don't navigate if save fails
        }
      }

      // Existing show - navigate to next step
      navigate(`/publish/${showId}/internal-details`);
    } else {
      // New show - proceed with creation
      setPendingAction('createAndContinue');
      await proceedWithCreate();
    }
  };

  const handleDuplicateConfirm = async () => {
    setShowDuplicateDialog(false);
    // Retry with force=true
    await proceedWithCreate(true);
  };

  const handleDuplicateCancel = () => {
    setShowDuplicateDialog(false);
    setPendingAction(null);
    setDuplicateShowInfo(null);
    setPendingShowData(null);
    // Nothing was created, user can continue editing
  };

  return (
    <PublishLayout
      onContinue={handlePublish}
      continueLabel={showId ? 'Continue' : 'Create & Continue'}
    >
      <div className={styles.contentWrapper}>
          {/* Page Title */}
          <div className={styles.pageHeader}>
            <h2 className={styles.pageTitle}>Show Details</h2>
            <div className={styles.headerActions}>
              <Button variant="ghost" size="sm" onClick={() => setShowShareModal(true)}>
                <Users className="h-4 w-4 mr-2" />
                Invite
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className={styles.iconButton}
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                }}
              >
                <Link2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className={styles.sectionSpacing}>
            {/* Content Type */}
            <section className="space-y-2">
              <div className={styles.contentTypeSection}>
                <div className={styles.contentTypeInfo}>
                  <Label>Content Type</Label>
                  <p className={styles.contentTypeDescription}>
                    Share your video content on Dashreel to reach a global audience
                  </p>
                </div>
                <div className={styles.contentTypeOptions}>
                  <div 
                    className={`relative cursor-pointer group`}
                    onClick={() => updateState({ contentType: 'dashreel' })}
                  >
                    <div className={`aspect-[4/3] w-32 bg-secondary/50 rounded-lg overflow-hidden flex items-center justify-center ${
                      state.contentType === 'dashreel' 
                      ? 'border-2 border-primary shadow-[0_0_0_1px_rgba(var(--primary),0.3)]' 
                      : 'border border-border hover:border-input hover:shadow-[0_0_0_1px_rgba(var(--primary),0.2)] group'
                    }`}>
                      <svg 
                        width="32" 
                        height="32" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg" 
                        className={`transition-colors duration-200 ${
                          state.contentType === 'dashreel'
                          ? 'text-primary'
                          : 'text-muted-foreground/70 group-hover:text-primary/60'
                        }`}
                      >
                        <path d="M2 12C2 7.286 2 4.929 3.464 3.464C4.93 2 7.286 2 12 2C16.714 2 19.071 2 20.535 3.464C22 4.93 22 7.286 22 12C22 16.714 22 19.071 20.535 20.535C19.072 22 16.714 22 12 22C7.286 22 4.929 22 3.464 20.535C2 19.072 2 16.714 2 12Z" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M21.5 8H2.5M10.5 2.5L7 8M17 2.5L13.5 8M15 14.5C15 13.867 14.338 13.44 13.014 12.585C11.672 11.719 11.001 11.286 10.5 11.605C10 11.922 10 12.781 10 14.5C10 16.219 10 17.078 10.5 17.396C11 17.714 11.672 17.281 13.014 16.415C14.338 15.60 15 15.133 15 14.5Z" stroke="currentColor" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div className="mt-2">
                      <h3 className="text-sm font-medium flex items-center gap-2">
                        Dashreel
                        {state.contentType === 'dashreel' && <Check className="h-4 w-4 text-primary" />}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Show Details */}
            <section className="space-y-6">
                <div className="grid grid-cols-2 gap-x-4 gap-y-6">
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1">
                      Title (Platform Name)
                      <span className="text-primary">*</span>
                    </Label>
                    <Input 
                      placeholder="Enter title" 
                      value={state.details.title || ''}
                      onChange={(e) => updateState({
                        details: { ...state.details, title: e.target.value }
                      })}
                      className={cn(formErrors.title && "border-red-500")}
                    />
                    {formErrors.title && (
                      <p className="text-xs text-red-500 mt-1">Title is required</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1">
                      Creator Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      placeholder="Enter creator name"
                      value={state.details.creatorName || ''}
                      onChange={(e) => updateState({
                        details: { ...state.details, creatorName: e.target.value }
                      })}
                    />
                    {formErrors.creatorName && (
                      <p className="text-xs text-red-500 mt-1">Creator name is required</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label>Author Name</Label>
                    <Input 
                      placeholder="Enter author name"
                      value={state.details.authorName || ''}
                      onChange={(e) => updateState({
                        details: { ...state.details, authorName: e.target.value }
                      })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Publisher Name</Label>
                    <Input 
                      placeholder="Enter publisher name"
                      value={state.details.publisherName || ''}
                      onChange={(e) => updateState({
                        details: { ...state.details, publisherName: e.target.value }
                      })}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="flex items-center gap-1">
                    Description
                  </Label>
                  <Textarea
                    value={state.details.description}
                    onChange={(e) => updateState({
                      details: { ...state.details, description: e.target.value }
                    })}
                    placeholder="Describe your show's story, theme, and audience..."
                    className="min-h-[120px] resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-6">
                  {/* Language and Show Type row */}
                  <div className="col-span-2 grid grid-cols-2 gap-4">
                    <div className="space-y-2 w-full">
                      <Label className="text-sm flex items-center gap-1">
                        Language
                        <span className="text-primary">*</span>
                      </Label>
                      <Select
                        value={state.details.language}
                        onValueChange={(value) => updateState({
                          details: { ...state.details, language: value }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select language" />
                        </SelectTrigger>
                        <SelectContent>
                          {languages.map((language) => (
                            <SelectItem key={language.value} value={language.value}>
                              {language.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formErrors.language && (
                        <p className="text-xs text-red-500 mt-1">Language is required</p>
                      )}
                    </div>
                    <div className="space-y-2 w-full">
                      <Label className="text-sm flex items-center gap-1">
                        Show Type
                        <span className="text-primary">*</span>
                      </Label>
                      <Select
                        value={state.details.showType}
                        onValueChange={(value) => updateState({
                          details: { ...state.details, showType: value }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select show type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ongoing">Ongoing</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                      {formErrors.showType && (
                        <p className="text-xs text-red-500 mt-1">Show type is required</p>
                      )}
                    </div>

                    {/* Content Origin Field */}
                    <div className="space-y-2 w-full">
                      <Label className="text-sm flex items-center gap-1">
                        Content Style
                        <span className="text-primary">*</span>
                      </Label>
                      <Select
                        value={state.details.contentOrigin}
                        onValueChange={(value) => updateState({
                          details: {
                            ...state.details,
                            contentOrigin: value,
                            // Reset visualStyle if changing away from ORIGINAL
                            visualStyle: value === 'ORIGINAL' ? state.details.visualStyle : ''
                          }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select content style" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ANIMATION">Animation</SelectItem>
                          <SelectItem value="LIVE_ACTION">Live Action</SelectItem>
                          <SelectItem value="ORIGINAL">Original</SelectItem>
                        </SelectContent>
                      </Select>
                      {formErrors.contentOrigin && (
                        <p className="text-xs text-red-500 mt-1">Content style is required</p>
                      )}
                    </div>

                    {/* Visual Style Field - Only enabled when Content Origin is ORIGINAL */}
                    <div className="space-y-2 w-full">
                      <Label className="text-sm flex items-center gap-1">
                        Visual Style
                        {state.details.contentOrigin === 'ORIGINAL' && (
                          <span className="text-primary">*</span>
                        )}
                      </Label>
                      <Select
                        value={state.details.visualStyle}
                        onValueChange={(value) => updateState({
                          details: { ...state.details, visualStyle: value }
                        })}
                        disabled={state.details.contentOrigin !== 'ORIGINAL'}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select visual style" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ANIMATION">Animation</SelectItem>
                          <SelectItem value="REALISTIC">Realistic</SelectItem>
                          <SelectItem value="LIVE_ACTION">Live Action</SelectItem>
                        </SelectContent>
                      </Select>
                      {formErrors.visualStyle && state.details.contentOrigin === 'ORIGINAL' && (
                        <p className="text-xs text-red-500 mt-1">Visual style is required when content origin is Original</p>
                      )}
                    </div>

                    {/* Platform Types — despite the name, this is APP (tenant) eligibility
                        (DashReels/ShortFree), not device platforms (Android/iOS/Web/TikTok Minis) */}
                    <div className="space-y-2 w-full">
                      <Label className="text-sm flex items-center gap-1">
                        Apps
                        <span className="text-primary">*</span>
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Which apps this show belongs to. Per-platform release control (Android, iOS, Web, TikTok Minis) lives on the Review &amp; Publish screen.
                      </p>
                      <Select
                        value={state.details.platformTypes.join(',')}
                        onValueChange={(value) => {
                          const current = state.details.platformTypes;
                          const platformValue = value as PlatformType;
                          const next = current.includes(platformValue)
                            ? current.filter((p) => p !== platformValue)
                            : [...current, platformValue];
                          if (next.length === 0) return;
                          updateState({ details: { ...state.details, platformTypes: next } });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue>
                            {state.details.platformTypes
                              .map((p) => p === PlatformType.DASHREEL ? 'DashReels' : 'ShortFree')
                              .join(', ')}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {[
                            { value: PlatformType.DASHREEL, label: 'DashReels' },
                            { value: PlatformType.SHORTFREE, label: 'ShortFree' },
                          ].map(({ value, label }) => (
                            <SelectItem key={value} value={value}>
                              {state.details.platformTypes.includes(value) ? `✓ ${label}` : label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Show Content Type */}
                    <div className="space-y-2 w-full">
                      <Label className="text-sm flex items-center gap-1">
                        Show Content Type
                      </Label>
                      <Select
                        value={state.details.showContentType}
                        onValueChange={(value) => updateState({
                          details: { ...state.details, showContentType: value as ShowContentType }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select show content type" />
                        </SelectTrigger>
                        <SelectContent>
                          {showContentTypeOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Subtitle Support and Languages row */}
                  {state.contentType === 'dashreel' && (
                    <>
                      <div className="flex items-center justify-between p-4 bg-secondary/40 rounded-lg">
                        <div className="space-y-0.5">
                          <Label>Subtitle Support</Label>
                          <p className="text-sm text-muted-foreground">Enable subtitles for this show</p>
                        </div>
                        <Switch
                          checked={state.details.hasSubtitleSupport}
                          onCheckedChange={(checked) => updateState({
                            details: { ...state.details, hasSubtitleSupport: checked }
                          })}
                        />
                      </div>
                      {state.details.hasSubtitleSupport && (
                        <div className="space-y-2">
                          <Label>Subtitle Languages</Label>
                          <Select
                            value={state.details.subtitleLanguages?.join(',') || ''}
                            onValueChange={(value) => updateState({
                              details: { ...state.details, subtitleLanguages: value ? value.split(',') : [] }
                            })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select subtitle languages" />
                            </SelectTrigger>
                            <SelectContent>
                              {languages.map((language) => (
                                <SelectItem key={language.value} value={language.value}>
                                  {language.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    Genres
                  </Label>
                  <GenreSelector
                    type="genres"
                    selectedGenres={state.details.genres || []}
                    onChange={(genres) => updateState({
                      details: { ...state.details, genres }
                    })}
                    placeholder="Select genres..."
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    Tropes
                  </Label>
                  <GenreSelector
                    type="tropes"
                    selectedGenres={state.details.tropes || []}
                    onChange={(tropes) => updateState({
                      details: { ...state.details, tropes }
                    })}
                    placeholder="Select tropes..."
                  />
                </div>

                <div className="space-y-2">
                  <Label>Tags</Label>
                  <Input
                    placeholder="Enter tags separated by commas (e.g., action, adventure, comedy)"
                    value={state.details.tags ? state.details.tags.join(', ') : ''}
                    onChange={(e) => {
                      const tagsString = e.target.value;
                      const tagsArray = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
                      updateState({
                        details: { ...state.details, tags: tagsArray }
                      });
                    }}
                  />
                </div>

                <div className="flex items-center justify-between gap-8">
                  <div className="flex-1">
                    <Label className="flex items-center gap-1">
                      Maturity Rating
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Set age-appropriate content rating
                    </p>
                  </div>
                  <div className="flex gap-4">
                    {maturityRatings.map((rating) => (
                      <div 
                        key={rating.value}
                        className={`relative cursor-pointer group`}
                        onClick={() => updateState({
                          details: { ...state.details, maturityRating: rating.value }
                        })}
                      >
                        <div className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          state.details.maturityRating === rating.value 
                            ? 'border-primary text-primary' 
                            : 'border-transparent bg-secondary hover:border-primary/40'
                        }`}>
                          <span className="text-sm font-medium">{rating.label}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actor Gender Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="font-medium">Actor Gender</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addActorGenderEntry}
                      className="flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add Character
                    </Button>
                  </div>

                  {state.details.actorGenderList.length > 0 && (
                    <div className="space-y-3">
                      {state.details.actorGenderList.map((entry, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                          <div className="flex-1">
                            <Input
                              placeholder="Character name"
                              value={entry.characterName}
                              onChange={(e) => updateActorGenderEntry(index, 'characterName', e.target.value)}
                            />
                          </div>
                          <div className="w-32">
                            <Select
                              value={entry.gender}
                              onValueChange={(value) => updateActorGenderEntry(index, 'gender', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={CharacterGender.MALE}>{CharacterGender.MALE}</SelectItem>
                                <SelectItem value={CharacterGender.FEMALE}>{CharacterGender.FEMALE}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => removeActorGenderEntry(index)}
                            className="h-8 w-8 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {state.details.actorGenderList.length === 0 && (
                    <p className="text-sm text-muted-foreground italic">
                      No character genders defined. Click "Add Character" to get started.
                    </p>
                  )}
                </div>

                {/* Other Settings */}
                <div className="space-y-4">
                  <Label className="font-medium">Other Settings</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-4 bg-secondary/40 rounded-lg">
                      <div className="space-y-0.5">
                        <Label>Trial Show</Label>
                        <p className="text-sm text-muted-foreground">Enable show testing before production</p>
                      </div>
                      <Switch
                        checked={state.details.isTrialShow}
                        onCheckedChange={(checked) => updateState({
                          details: { ...state.details, isTrialShow: checked }
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-secondary/40 rounded-lg">
                      <div className="space-y-0.5">
                        <Label>Fast Pass Enabled</Label>
                        <p className="text-sm text-muted-foreground">Unlock last 10 episodes with Fast Pass</p>
                      </div>
                      <Switch
                        checked={state.details.isFastPassEnabled}
                        onCheckedChange={(checked) => updateState({
                          details: { ...state.details, isFastPassEnabled: checked }
                        })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div className="flex items-center justify-between p-4 bg-secondary/40 rounded-lg">
                      <div className="space-y-0.5">
                        <Label>AI Show</Label>
                        <p className="text-sm text-muted-foreground">Mark as AI-generated content</p>
                      </div>
                      <Switch
                        checked={state.details.isAIShow}
                        onCheckedChange={(checked) => updateState({
                          details: { ...state.details, isAIShow: checked }
                        })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div className="space-y-2">
                      <Label>Origin Country</Label>
                      <CountrySelect
                        value={state.details.originCountry}
                        onChange={(country) => updateState({
                          details: { ...state.details, originCountry: country }
                        })}
                        placeholder="Select country of origin"
                        showFlags={true}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select
                        value={state.details.category}
                        onValueChange={(value) => updateState({
                          details: { ...state.details, category: value }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="vip">VIP</SelectItem>
                          <SelectItem value="blockbuster">Blockbuster</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Whitelisted countries</Label>
                      <Select
                        value={state.details.whitelist}
                        onValueChange={(value) => {
                          if (value === 'new') {
                            // Redirect to whitelist creation page with showId
                            navigate(`/whitelist/create?showId=${showId}`);
                          } else {
                            updateState({
                              details: { ...state.details, whitelist: value }
                            });
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select whitelist category" />
                        </SelectTrigger>
                        <SelectContent>
                          {/* Dynamic whitelists from database (includes seeded presets) */}
                          {whitelists.map(wl => (
                            <SelectItem key={wl.id} value={wl.id}>
                              {wl.displayName}
                            </SelectItem>
                          ))}
                          <SelectItem value="new" className="text-primary">Create New</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Empty container */}
                <div className={styles.emptyContainer} />
            </section>
          </div>
        </div>
      <ShareModal open={showShareModal} onOpenChange={setShowShareModal} />

      {/* Duplicate Title Warning Dialog */}
      <AlertDialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Duplicate Show Title
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                A show with the title <strong>"{state.details.title}"</strong> already exists.
              </p>
              {duplicateShowInfo && duplicateShowInfo.creator && (
                <div className="bg-secondary/50 p-3 rounded-md text-sm">
                  <p>Created by: <strong>{duplicateShowInfo.creator}</strong></p>
                </div>
              )}
              <p className="text-sm">
                Do you still want to continue creating a new show with this title?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDuplicateCancel}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDuplicateConfirm}>
              Continue Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PublishLayout>
  );
}
